import React, { useState, useEffect } from "react";
import { DollarSign, PercentIcon } from "lucide-react";

interface FormattedNumberInputProps {
  value: number;
  onChange: (num: number | "") => void;
  placeholder?: string;
  prefix?: "dollar" | "percent" | "none";
  className?: string;
  min?: number;
  max?: number;
  step?: number;
}

function formatNumber(value: string | number): string {
  const number =
    typeof value === "number" ? value : parseFloat(value.replace(/,/g, ""));
  if (isNaN(number)) return "";
  return number.toLocaleString();
}

export const FormattedNumberInput: React.FC<FormattedNumberInputProps> = ({
  value,
  onChange,
  placeholder = "e.g., 1,000",
  prefix = "dollar",
  className = "",
  min,
  max,
  step,
}) => {
  const [displayValue, setDisplayValue] = useState(formatNumber(value));

  // Update display value when value prop changes
  useEffect(() => {
    setDisplayValue(formatNumber(value));
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDisplayValue(raw);

    const numericString = raw.replace(/[^0-9.-]/g, ""); // Remove non-numeric characters
    const parsed = parseFloat(numericString);
    if (!isNaN(parsed)) {
      // Apply min/max constraints if provided
      const constrainedValue = Math.min(
        Math.max(parsed, min ?? -Infinity),
        max ?? Infinity
      );
      onChange(constrainedValue);
    }
    if (numericString === "") {
      onChange("");
    }
  };

  const handleBlur = () => {
    // Re-format input on blur
    const numericString = displayValue.replace(/[^0-9.-]/g, "");
    const parsed = parseFloat(numericString);
    if (!isNaN(parsed)) {
      // Apply min/max constraints if provided
      const constrainedValue = Math.min(
        Math.max(parsed, min ?? -Infinity),
        max ?? Infinity
      );
      setDisplayValue(formatNumber(constrainedValue));
      onChange(constrainedValue);
    } else {
      setDisplayValue("");
    }
  };

  const renderPrefix = () => {
    if (prefix === "none") return null;
    return (
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {prefix === "dollar" ? (
          <DollarSign size={18} className="text-gray-400" />
        ) : (
          <PercentIcon size={18} className="text-gray-400" />
        )}
      </div>
    );
  };

  return (
    <div className="relative">
      {renderPrefix()}
      <input
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`input-field ${
          prefix !== "none" ? "pl-10" : ""
        } w-full ${className}`}
        min={min}
        max={max}
        step={step}
      />
    </div>
  );
};
